import { action, computed, observable, runInAction } from "mobx";
import EventViewStore from "../event/EventViewStore";
import { AdditionalOfferViewStore } from "../shared/AdditionalOfferViewStore";
import { SportsStatsApiService } from "@api-services/sport-stats";
import {
	TeamStandingsRequestModel,
	TeamStandingsResponse,
	Standing,
	PromotionPhase,
	TeamStandingsData,
} from "@data-types/sport-stats/SportStatsTypes";
import { LoaderStore } from "@state/stores/common";
import PressureIndexStore from "./PressureIndexStore";
import { Subscription } from "rxjs";
import { getCurrentLanguage } from "@utils";
import { RequestType } from "../../../../data/types/sport-stats/SportStatsTypes";
import { Team } from "@gp/models";

export const availableTeamStandingsMobileFilterOptions = [
	{
		key: "full",
		title: "EVENT.STATISTICS.TEAM_STANDINGS.TABLE.FULL",
	},
	{
		key: "short",
		title: "EVENT.STATISTICS.TEAM_STANDINGS.TABLE.SHORT",
	},
	{
		key: "last_five",
		title: "EVENT.STATISTICS.BETTING_TYPE_STATS.LAST_FIVE",
	},
];

export const availableTeamStandingsDesktopFilterOptions = [
	{
		key: "full",
		title: "EVENT.STATISTICS.TEAM_STANDINGS.TABLE.FULL",
	},
	{
		key: "short",
		title: "EVENT.STATISTICS.TEAM_STANDINGS.TABLE.SHORT",
	},
];

export const teamStandingsTabs = [
	{ key: "total", title: "EVENT.STATISTICS.TEAM_STANDINGS.BUTTONS.ALL" },
	{ key: "home", title: "EVENT.OFFER.COMPETITOR_HOME" },
	{ key: "away", title: "EVENT.OFFER.COMPETITOR_AWAY" },
];

export default class TeamStandingsStatsStore {
	eventStore: EventViewStore | AdditionalOfferViewStore;
	pressureIndex: PressureIndexStore;
	loader: LoaderStore = new LoaderStore();

	@observable isTeamStandingsStatsActive = false;
	@observable filter: TeamStandingsRequestModel | null;
	@observable teamStandingsStats: TeamStandingsResponse | null = null;
	@observable teamStandingsData: TeamStandingsData[] | undefined;
	@observable isError = false;
	@observable isFetchingData = false;
	@observable isTeamStandingsAvailable = false;
	@observable activeTab: string = "total" as "total" | "home" | "away";
	@observable teamStandingsFilter: string = "full" as "full" | "short";
	@observable teamStandingsTournamentGroup: string = "all";
	@observable
	teamStandingsStatsPromotions: { name: string; phase: PromotionPhase }[] =
		[];

	@observable tournamentIds: string[] = [];
	@observable currentTournamentId: string | undefined;

	private dataSubscription?: Subscription | null = null;

	constructor(eventStore: EventViewStore | AdditionalOfferViewStore) {
		this.eventStore = eventStore;
	}

	@action.bound
	async initializeLiveTeamStandingsStatistics() {
		this.isFetchingData = true;

		try {
			this.updateTournamentIds();

			await this.fetchTeamStandingsStats();

			if (
				this.previousTournamentId &&
				this.previousTournamentId !== this.currentTournamentId
			) {
				this.disposeDataSubscription();
			}

			if (
				this.dataSubscription &&
				this.currentTournamentId ===
					this.tournamentIds[this.tournamentIds.length - 1]
			) {
				return;
			}

			if (
				this.teamStandingsStats &&
				this.teamStandingsStats.data?.teamStanding[0]?.isTournamentLive
			) {
				this.disposeDataSubscription();

				const filter = this.generateSubscriptionFilter();
				if (!filter) {
					return;
				}

				this.dataSubscription = this.eventStore.rootStore.sportStatHub
					?.getSubscription(filter)
					.subscribe({
						next: (data: any) => {
							if (Array.isArray(data) && data.length > 0) {
								if (
									data[0].data &&
									Array.isArray(data[0].data.teamStanding)
								) {
									this.teamStandingsStats = data[0];
								} else {
									console.warn(
										"Missing live team standings statistics"
									);
								}
							}
						},
						error: (e: any) => {
							console.error(
								"Live team standings subscription error:",
								e
							);
							this.isError = true;
						},
					});
			}
		} catch (error) {
			console.error(
				"Error initializing live team standings statistics:",
				error
			);
			this.isError = true;
		} finally {
			this.isFetchingData = false;
		}
	}

	@action.bound
	async fetchTeamStandingsStats() {
		const filter = this.createTeamStandingsStatsFilter();

		if (!filter) {
			console.warn("Invalid filter for team standings stats.");
			return;
		}

		this.loader.suspend();

		try {
			const teamStandingsApiResponse =
				await SportsStatsApiService.fetchTeamStandingsStatistic(filter);

			runInAction(() => {
				if (
					teamStandingsApiResponse.statusCode === 2000000000 &&
					teamStandingsApiResponse.response
				) {
					this.teamStandingsStats = teamStandingsApiResponse
						.response[0] as unknown as TeamStandingsResponse;
					this.isTeamStandingsAvailable = true;
				} else {
					console.warn(
						`Unexpected response or missing data for team standings. Status: ${teamStandingsApiResponse.statusCode}`
					);
					this.teamStandingsStats = null;
					this.isError = false;
				}
			});
		} catch (error) {
			console.error(error);
			this.teamStandingsStats = null;
			this.isTeamStandingsAvailable = false;
			this.isError = true;
		} finally {
			this.loader.resume();
		}
	}

	@action.bound
	getCurrentTeamStandingsStats(filter: string) {
		if (this.activeTab === filter) {
			const filteredTeamStandings =
				this.teamStandingsStats?.data?.teamStanding?.filter(
					(teamStanding) => teamStanding.standingsType === filter
				);
			return filteredTeamStandings?.[0]?.standings;
		}
		return [];
	}

	@action.bound
	getCurrentTeamStandingsStatsGroups(
		filter: string,
		tournamentGroup: string
	) {
		if (this.activeTab === filter && this.teamStandingsStats?.isGroupType) {
			const filteredTeamStandings =
				this.teamStandingsStats?.data?.teamStanding?.filter(
					(teamStanding) =>
						teamStanding.standingsType === filter &&
						(tournamentGroup === "all" ||
							teamStanding.groupName === tournamentGroup)
				);

			return (this.teamStandingsData = filteredTeamStandings ?? []);
		}
		return [];
	}

	@action.bound
	setActiveTab(activeTab: string = "total" as "total" | "home" | "away") {
		this.activeTab = activeTab;
	}

	@action.bound
	setTeamStandingsFilter(filter: string = "full" as "full" | "short") {
		this.teamStandingsFilter = filter;
	}

	@action.bound
	setTeamStandingsTournamentGroup(filter: string) {
		this.teamStandingsTournamentGroup = filter;
	}

	@action.bound
	handleTeamStandingsDropdownSelection(clickedValue: string) {
		const isFilter = availableTeamStandingsDesktopFilterOptions.some(
			(g) => g.key === clickedValue
		);

		const isTable = this.availableTeamStandingsTabs.some(
			(g) => g.key === clickedValue
		);

		const isTournamentGroup =
			this.availableTeamStandingsTournamentGroups.some(
				(g) => g === clickedValue
			);

		if (isFilter) {
			this.setTeamStandingsFilter(clickedValue);
		}

		if (isTable) {
			this.setActiveTab(clickedValue);
		}

		if (isTournamentGroup) {
			this.setTeamStandingsTournamentGroup(clickedValue);
		}
	}

	@action.bound
	extractUniquePromotions(standings?: Standing[]) {
		if (!standings) {
			this.teamStandingsStatsPromotions = [];
			return;
		}

		const promotion = new Set<string>();
		const promotions: { name: string; phase: PromotionPhase }[] = [];

		for (const s of standings) {
			const name = s.promotion?.name;
			const phase = s.promotion?.phase;
			if (!name || !phase) continue;

			const key = `${name}-${phase}`;
			if (!promotion.has(key)) {
				promotion.add(key);
				promotions.push({ name, phase });
			}
		}
		this.teamStandingsStatsPromotions = promotions;
		return this.teamStandingsStatsPromotions;
	}

	@action.bound
	updateTournamentIds() {
		const eventData = this.eventStore.sportStatisticsStore.getEventData();
		this.currentTournamentId = eventData?.event?.tournamentId;

		if (!this.currentTournamentId) {
			return;
		}

		this.tournamentIds.push(this.currentTournamentId);
		if (this.tournamentIds.length > 2) {
			this.tournamentIds.shift();
		}
	}

	@action.bound
	toggleTeamStandingsStats() {
		this.activeTab = "total";
		this.teamStandingsFilter = "full";
		this.teamStandingsTournamentGroup = "all";
		this.isTeamStandingsStatsActive = !this.isTeamStandingsStatsActive;
	}

	getTeamMatch(
		standing: Standing,
		homeTeam: Team | undefined,
		awayTeam: Team | undefined,
		hasBothTeamsInStanding: boolean
	) {
		const homeMatch = homeTeam?.id === standing.id;
		const awayMatch = awayTeam?.id === standing.id;

		return {
			isHome: !!hasBothTeamsInStanding && homeMatch,
			isAway: !!hasBothTeamsInStanding && awayMatch,
			isHighlighted: !!hasBothTeamsInStanding && (homeMatch || awayMatch),
		};
	}

	@computed
	get isTeamStandingsStatsAvailable(): boolean {
		if (!this.teamStandingsStats) {
			return false;
		}
		return true;
	}

	@computed
	get availableTeamStandingsTournamentGroups() {
		const filteredTeamStandingsTournamentGroupTabs = Array.from(
			new Set(
				this.teamStandingsStats?.data.teamStanding.map(
					(group: any) => group.groupName
				)
			)
		);

		return ["all", ...filteredTeamStandingsTournamentGroupTabs];
	}

	@computed
	get availableTeamStandingsTabs() {
		if (!this.teamStandingsStats) {
			return [];
		}

		const standings = this.teamStandingsStats?.data?.teamStanding;

		return teamStandingsTabs.filter((tab) =>
			standings.some((s) => s.standingsType === tab.key)
		);
	}

	@computed
	get previousTournamentId(): string | undefined {
		return this.tournamentIds.length > 1
			? this.tournamentIds[this.tournamentIds.length - 2]
			: undefined;
	}

	@computed
	get hasBothTeamsInStanding(): boolean {
		const standings =
			this.teamStandingsStats?.data?.teamStanding?.[0]?.standings;
		const homeExists = standings?.some(
			(s) => s.id === this.eventStore.teamOne?.id
		);
		const awayExists = standings?.some(
			(s) => s.id === this.eventStore.teamTwo?.id
		);
		return !!homeExists && !!awayExists;
	}

	createTeamStandingsStatsFilter() {
		const eventData = this.eventStore.sportStatisticsStore.getEventData();

		if (!eventData || !eventData.event.id) {
			console.warn("Event is required for team standings statistics.");
			return null;
		}

		return {
			specific: {
				RequestType: RequestType.Data,
				TournamentId: eventData.tournament.id,
				EventId: eventData.event.id,
			},
		};
	}

	generateSubscriptionFilter() {
		if (this.isError) {
			return;
		}
		const eventFilter = this.createTeamStandingsStatsFilter();

		if (!eventFilter) {
			return;
		}

		return {
			language: getCurrentLanguage(),
			subscriptionId: `tournament-stats-${eventFilter?.specific.TournamentId}-${eventFilter?.specific.EventId}`,
			environment: WEBPACK_ENVIRONMENT_KEY,
			typeAbrv: "TeamStanding",
			filterSpecific: {
				RequestType: RequestType.Data,
				Events: [
					{
						EventId: eventFilter.specific.EventId,
						LastMinuteReceived: 0,
					},
				],
				TournamentId: eventFilter.specific.TournamentId,
			},
		};
	}

	@action.bound
	disposeDataSubscription() {
		if (this.dataSubscription != null) {
			this.dataSubscription.unsubscribe();
			this.dataSubscription = null;
		}
	}

	@action.bound
	onDispose() {
		this.teamStandingsStats = null;
		this.teamStandingsData = [];
		this.teamStandingsStatsPromotions = [];
		this.isError = false;
		this.isFetchingData = false;
		this.disposeDataSubscription();
	}
}
